import {Suspense, useEffect, FormEvent, useRef, useState} from "react"
import {LoadingPage} from "@indebted/components/Loading"
import {ErrorPage} from "@indebted/components/Error"
import Grid from "@mui/material/Grid"
import {Button, Typography, useMediaQuery, useTheme} from "@mui/material"
import {Trans, useTranslation} from "react-i18next"
import {ErrorBoundary} from "react-error-boundary"
import {PageTitle, NewLayoutPage} from "@indebted/components/Page"
import {Panel} from "@indebted/components/Panel"
import {useNavigate, useSearchParams} from "react-router-dom"
import {useAnalytics} from "@indebted/analytics"
import {useQuery, UseQueryResult, useSuspenseQuery} from "@tanstack/react-query"
import {confirmSetup, stripeQuery} from "@indebted/stripe-payment-arrangement/PaymentMethodForm"
import {useNotification} from "@indebted/components/Notification"
import {StripeError} from "@stripe/stripe-js"
import {getEFTAPage} from "@indebted/api/paymentArrangement/efta"
import {styled} from "@mui/material/styles"

import {formatDate} from "../../../src/dateFormatter.ts"
import {ValidLocale} from "../../../src/Locales.ts"
import {formatCurrency} from "../../../src/currencyFormatter.ts"
import {Breadcrumbs} from "../Breadcrumbs.tsx"
import {nextPage} from "../Navigator"
import {EFTAPageMainResponse, EFTAPageResponse} from "../types"
import {configurePaymentMethodOnSuccess} from "../utils/configure-payment-method"
import {usePaymentArrangementId} from "../usePaymentArrangementId"

import {JumpToBottomButton} from "./JumpButton.tsx"

export function EFTAPage() {
	return (
		<ErrorBoundary
			fallbackRender={({error}) => {
				return <ErrorPage message={error.Message || error.message} error={error} />
			}}
		>
			<Suspense fallback={<LoadingPage />}>
				<EFTAPageLoader />
			</Suspense>
		</ErrorBoundary>
	)
}

function EFTAPageLoader() {
	const navigate = useNavigate()
	const id = usePaymentArrangementId()
	const [searchParams] = useSearchParams()
	const stripeClientSecret = searchParams.get("clientSecret")
	const stripeConfirmationToken = searchParams.get("token")
	const hasRequiredParams = !(!stripeClientSecret || !stripeConfirmationToken)

	useEffect(() => {
		if (!hasRequiredParams) {
			navigate(nextPage("payment-method", id))
		}
	}, [navigate, id, hasRequiredParams])

	const result: UseQueryResult<EFTAPageResponse, Error> = useQuery({
		// eslint-disable-next-line @tanstack/query/exhaustive-deps
		queryKey: ["efta", id, stripeConfirmationToken], // We don't hasRequiredParams in the query key because the query is only enabled when hasRequiredParams is true
		queryFn: () => {
			if (!hasRequiredParams) throw new Error("Missing required params")
			return getEFTAPage(id, stripeConfirmationToken)
		},
		enabled: hasRequiredParams,
	})

	if (result.data?.type === "redirect") {
		navigate(nextPage(result.data.Next, id))
		return <LoadingPage />
	}

	if (!hasRequiredParams || !result.data) {
		return <LoadingPage />
	}

	return (
		<EFTA
			data={result.data}
			paymentArrangementID={id}
			stripeClientSecret={stripeClientSecret}
			stripeConfirmationToken={stripeConfirmationToken}
		/>
	)
}

function EFTA({
	data,
	paymentArrangementID,
	stripeClientSecret,
	stripeConfirmationToken,
}: {
	data: EFTAPageMainResponse
	paymentArrangementID: string
	stripeClientSecret: string
	stripeConfirmationToken: string
}) {
	const {t, i18n} = useTranslation()
	const {revenue} = useAnalytics()
	const navigate = useNavigate()
	const {notification} = useNotification()
	const stripe = useSuspenseQuery(stripeQuery(data.PublishableKey, t))
	const isDebitCard = data.PaymentMethod === "DebitCard"
	const theme = useTheme()
	const isSmallScreen = useMediaQuery(theme.breakpoints.down(599))
	const [showStickyButton, setShowStickyButton] = useState(false)
	const buttonObserverRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!buttonObserverRef.current) return

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => setShowStickyButton(isSmallScreen && !entry.isIntersecting))
			},
			// Trigger when button area is fully visible/invisible
			{threshold: 1, rootMargin: "16px"},
		)

		observer.observe(buttonObserverRef.current)

		return () => observer.disconnect()
	}, [isSmallScreen])

	const submitEFTA = async (event: FormEvent<HTMLButtonElement>) => {
		event.preventDefault()

		try {
			const response = await confirmSetup(stripe.data, stripeClientSecret, stripeConfirmationToken, t)
			const next = await configurePaymentMethodOnSuccess({
				PaymentMethodID: response.setupIntent.payment_method as string,
				ProviderRawData: response,
				ProviderSetupID: response.setupIntent.id,
				PaymentArrangementID: paymentArrangementID,
				navigate,
			})

			revenue(data.InstalmentAmount, data.Currency, "PaymentPlan", {
				frequency: data.Frequency,
			})
			navigate(nextPage(next, paymentArrangementID))
		} catch (error) {
			notification.error(
				(error as unknown as StripeError)?.message || (error as unknown as {Message: string})?.Message,
				5000,
			)
			console.error("Failed to confirm setup", error)
			navigate(nextPage(data.PaymentFormPage, paymentArrangementID))
		}
	}

	return (
		<NewLayoutPage>
			<JumpToBottomButton hasStickyButton={showStickyButton} bottomTolerance={64} />
			<Grid container direction="column" alignItems="stretch" spacing={5} padding={0}>
				<Grid>
					<Breadcrumbs
						currentStep={t("breadcrumbs.efta")}
						items={[{label: t("breadcrumbs.payment_details"), href: "../payment-method"}]}
					/>
				</Grid>
				<Grid>
					<PageTitle>{t("payment_arrangement_efta.header")}</PageTitle>
				</Grid>
				<Panel>
					<Grid
						container
						direction="row"
						spacing={3}
						style={{alignItems: "center", justifyContent: "flex-start"}}
					>
						<Grid size={{xs: 9, sm: 10}} style={{paddingTop: "0.75rem"}}>
							<Typography variant="subtitle1" style={{fontWeight: "bold", lineHeight: 1.3}} align="left">
								{t(
									isDebitCard
										? "payment_arrangement_efta.debit_card.efta_disclosure.title"
										: "payment_arrangement_efta.direct_debit.efta_disclosure.title",
								)}
							</Typography>
						</Grid>
					</Grid>
					<Grid>
						<Trans
							i18nKey={
								isDebitCard
									? "payment_arrangement_efta.debit_card.efta_disclosure.text"
									: "payment_arrangement_efta.direct_debit.efta_disclosure.text"
							}
							values={{
								PaymentPlanInstalmentAmount: formatCurrency(
									data.InstalmentAmount / 100,
									data.Currency,
									i18n.language as ValidLocale,
								),
								PaymentPlanFrequency: data.Frequency,
								PaymentPlanStartDate: formatDate(new Date(data.StartDate)),
								PaymentPlanEndDate: formatDate(new Date(data.EndDate)),
								ClientName: data.ClientName,
								Last4CardDigits: data.PaymentMethodLast4Digits,
								Last4BankAccountDigits: data.PaymentMethodLast4Digits,
								RoutingNumber: data.RoutingNumber,
								EntityName: data.EntityName,
							}}
							components={{
								FirstText: <DisclosureTextWithPadding variant="body1" />,
								SecondText: <DisclosureTextWithPadding variant="body1" />,
								a: <a />,
							}}
						/>
					</Grid>
					<Grid>
						<DisclosureText color="textSecondary" variant="body1" sx={{fontWeight: "400"}}>
							<Trans
								i18nKey={
									isDebitCard
										? "payment_arrangement_efta.debit_card.efta_disclosure.agreement_text"
										: "payment_arrangement_efta.direct_debit.efta_disclosure.agreement_text"
								}
								values={{
									Last4BankAccountDigits: data.PaymentMethodLast4Digits,
									RoutingNumber: data.RoutingNumber,
									EntityName: data.EntityName,
								}}
								components={{a: <a />}}
							/>
						</DisclosureText>
					</Grid>
					{showStickyButton ? (
						<ButtonContainer
							sx={{paddingTop: "1.5rem"}}
							container
							alignItems="center"
							justifyContent="center"
						>
							<Button fullWidth type="submit" onClick={submitEFTA}>
								{t("payment_arrangement_efta.efta_disclosure.primary_button_label")}
							</Button>
						</ButtonContainer>
					) : null}
					<Grid
						sx={{paddingTop: "1.5rem"}}
						container
						alignItems="center"
						justifyContent="center"
						ref={buttonObserverRef}
					>
						<Button fullWidth type="submit" onClick={submitEFTA}>
							{t("payment_arrangement_efta.efta_disclosure.primary_button_label")}
						</Button>
					</Grid>
				</Panel>
			</Grid>
		</NewLayoutPage>
	)
}

const ButtonContainer = styled(Grid)`
	position: fixed !important;
	bottom: 0;
	left: 0;
	right: 0;
	gap: 16px;
	background-color: #ffffff;
	padding: 24px 16px !important;
	border-radius: 8px 8px 0 0;
	box-shadow: 0 4px 24px 0 rgba(54, 59, 100, 0.2);
	z-index: 1000;
	margin: 0 !important;
`

const DisclosureText = styled(Typography)`
	color: #000244;
	font-weight: 500;
`
const DisclosureTextWithPadding = styled(DisclosureText)`
	padding-bottom: 8px;
	padding-top: 8px;
`
